import React from 'react';
import { translate, Interpolate } from 'react-i18next';
import { get as _get } from 'lodash';

import { getStores } from '../../client/stores';
import ConnectionsContainer from './client/components/connection-list/connections-container';
import noListsImage from './assets/Lists Illustrations.png';
import Footnote from './client/components/footnote';
import { integrationStatus } from '../../client/api';
import { handleError, getInstance, getConfigedAxiosFunctions } from '../utils';

import { Text, TextButton } from 'wix-style-react';

export const mailChimpApiBaseUrl = (apiBaseUrl) => `${apiBaseUrl}mail-chimp/`;

export function initMailChimpActions({ api, key, config }) {
  const { biLogger, mailchimp } = getStores();
  const apiBaseUrl = mailChimpApiBaseUrl(config.apiBaseUrl);

  let getData;
  let showDialog;

  async function updateStatus(instance) {
    let status = integrationStatus.NEW;

    instance =
      instance ||
      (await api
        .getIntegrationInstance()
        .catch((err) => handleError(api, key, err)));
    if (instance) {
      const { getData: _getData } = getConfigedAxiosFunctions(
        apiBaseUrl,
        instance,
      );
      const { hasAuthToken } = await _getData({
        path: 'hasAuthToken',
      }).catch((err) => handleError(api, key, err));

      if (hasAuthToken) {
        status = integrationStatus.LOGGEDIN;
        const { connections } = await _getData({ path: `setup` }).catch((err) =>
          handleError(api, key, err),
        );

        if (connections && connections.length > 0) {
          const isConnected = connections.some(
            (connection) => connection.valid === true,
          );
          status = isConnected
            ? integrationStatus.CONNECTED
            : integrationStatus.DISCONNECTED;
        }
      }
    }
    api.setIntegrationProperty(key, 'status', status);
    return status;
  }

  const firstTimeExperienceAction = {
    type: 'edit',
    label: 'mailchimp.details-page.cta.title.MI1.5',
    handler: firstTimeExperienceHandler,
  };

  const loggedInAction = {
    type: 'edit',
    label: 'mailchimp.details-page.cta.title.MI1.5',
    handler: editConnections,
  };

  const editAction = {
    type: 'edit',
    label: 'details-page.popover.edit',
    handler: editConnections,
  };

  const getActions = (status) =>
    status === integrationStatus.NEW
      ? [firstTimeExperienceAction]
      : status === integrationStatus.LOGGEDIN
      ? [loggedInAction]
      : [editAction];

  function firstTimeExperienceHandler({ oauthRedirectUri, clientId }) {
    openMailChimpAuthModal({ api, oauthRedirectUri, clientId }).then(() =>
      editConnections(),
    );
  }

  function editConnections() {
    getInstance(api)
      .then(init)
      .then(() => showDialog());
  }

  function init(instance) {
    const isInitialized = () => [getData, showDialog].every((i) => i);

    const bootstrap = ({
      instance: _instance,
      biLogger: _biLogger,
      mailchimp: _mailchimp,
    }) => {
      const {
        getData: _getData,
        deleteData,
        postData,
      } = getConfigedAxiosFunctions(apiBaseUrl, _instance);

      const API = {
        saveConnection: (wixLabelId, mailchimpListId) =>
          postData({
            path: `connection`,
            params: {
              labelId: wixLabelId,
              listId: mailchimpListId,
            },
          }),
        deleteConnection: (uid) => deleteData({ path: `connection/${uid}` }),
        getConnections: () => _getData({ path: `connections` }),
      };
      _mailchimp.setAPI(API);

      function _showDialog() {
        const biData = { integrationType: key };

        const openNoListsModal = (openConnenctionsModal) => {
          biData.modalType = 'mailchimp no list';
          api.openNotificationModal({
            illustration: <img src={noListsImage} alt="" />,
            title: 'mailchimp.no.mailchimp.lists.title',
            subTitle: 'mailchimp.no.mailchimp.lists.sub.title',
            buttons: {
              primary: {
                text: 'mailchimp.notification.go.to.mailchimp',
                onClick: () =>
                  window.open(`https://www.mailchimp.com`, '_blank'),
              },
              secondary: {
                text: 'mailchimp.notification.refresh',
                onClick: (modalAPI) => {
                  modalAPI.setLoading(true);
                  _getData({ path: 'lists' })
                    .then(({ total_items: totalItems, lists }) => {
                      modalAPI.setLoading(false);
                      if (totalItems > 0) {
                        _mailchimp.setMailChimpContactLabels(
                          lists.reduce(
                            (prev, curr) => [
                              ...prev,
                              { id: curr.id, name: curr.name },
                            ],
                            [],
                          ),
                        );
                        modalAPI.close();
                        openConnenctionsModal();
                      }
                    })
                    .catch((err) => {
                      modalAPI.setLoading(false);
                      console.error(`Error getting mailchimp lists ${err}`);
                    });
                },
              },
              x: {
                onClick: () =>
                  biLogger.integrationModalCloseViaCloseBar(biData),
              },
            },
            onOpened: () => biLogger.integrationModalView(biData),
            onClosed: () => biLogger.integrationModalClosed(biData),
            biData: {
              ...biData,
              primaryCtaType: 'go to mailchimp',
              secondaryCtaType: 'refresh',
            },
          });
        };

        const SubTitle = translate()(({ t }) => (
          <div>
            <Text secondary>
              <Interpolate
                i18nKey="mailchimp.choose.contacts.modal.sub.title"
                useDangerouslySetInnerHTML
              />
            </Text>
            &nbsp;
            <TextButton
              onClick={() => {
                window.open(
                  t('mailchimp.choose.contacts.modal.sub.title.cta.link'),
                  '_blank',
                );
              }}
            >
              {t('mailchimp.choose.contacts.modal.sub.title.cta.title')}
            </TextButton>
          </div>
        ));
        openConnectionsModal();

        function openConnectionsModal() {
          biData.modalType = 'mailchimp connections';
          const biDataWithConnectionsData = { ...biData };
          api.openFlowModal({
            title: 'mailchimp.choose.contacts.modal.title',
            subTitleNode: <SubTitle />,
            contentNode: <ConnectionsContainer integrationKey={key} />,
            footnoteNode: <Footnote biData={biData} />,
            buttons: {
              primary: {
                text: 'modal.button.close',
                onClick: async (modalAPI) => {
                  await updateButtons();
                  modalAPI.close();
                  _biLogger.integrationModalCtaClick({
                    ...biData,
                    ctaType: 'close',
                  });
                },
              },
              x: {
                onClick: async () => {
                  _biLogger.integrationModalCloseViaCloseBar(biData);
                  await updateButtons();
                },
              },
            },
            onOpened: (modalAPI) => {
              modalAPI.setLoading(true);
              _getData({ path: `setup` })
                .then((settings) => {
                  _mailchimp.setupConnections(
                    Object.values(settings.connections) || [],
                  );
                  _mailchimp.setWixContactLabels(settings.wixContactLabels);
                  _mailchimp.setMailChimpContactLabels(
                    settings.mailChimpContactLabels,
                  );
                  biDataWithConnectionsData.data = {
                    listsLength: _mailchimp.mailChimpContactLabels.length,
                    existingConnectionsNumber:
                      _mailchimp.connectionsArray.length,
                  };
                  modalAPI.setLoading(false);
                  if (_mailchimp.mailChimpContactLabels.length === 0) {
                    modalAPI.close();
                    openNoListsModal(openConnectionsModal);
                  } else {
                    _biLogger.integrationModalView(biDataWithConnectionsData);
                  }
                })
                .catch((err) => {
                  modalAPI.setLoading(false);
                  modalAPI.close();
                  handleError(api, key, err);
                });
            },
            onClosed: () =>
              _biLogger.integrationModalClosed(biDataWithConnectionsData),
          });

          async function updateButtons() {
            const status = await updateStatus(_instance);
            const actions = getActions(status);
            api.setIntegrationProperty(key, 'actions', actions);
          }
        }
      }

      return { showDialog: _showDialog, getData: _getData };
    };

    if (!isInitialized()) {
      const bootstrapData = bootstrap({ instance, biLogger, mailchimp });
      showDialog = bootstrapData.showDialog;
      getData = bootstrapData.getData;
    }
  }

  async function _mainAction({ newUser, oauthRedirectUri, clientId }) {
    let authenticatedUser;

    const isAuthenticated = () => getData({ path: 'hasAuthToken' });

    const authenticateAndShowDialog = () =>
      openMailChimpAuthModal({ api, oauthRedirectUri, clientId })
        .then(() => getInstance(api))
        .then(init)
        .then(() => {
          authenticatedUser = true;
          showDialog();
        })
        .catch(() => {
          authenticatedUser = false;
        });

    if (!authenticatedUser && newUser) {
      authenticateAndShowDialog();
    } else {
      getInstance(api)
        .then(init)
        .then(isAuthenticated)
        .then(({ hasAuthToken }) =>
          hasAuthToken ? showDialog() : authenticateAndShowDialog(),
        );
    }
  }

  function openMailChimpAuthModal({ api: _api, oauthRedirectUri, clientId }) {
    return new Promise((resolve, reject) => {
      const redirectTo = encodeURIComponent(
        oauthRedirectUri + '?metaSiteId=' + _api.getMetaSiteId(),
      );
      const authUrl = `https://login.mailchimp.com/oauth2/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectTo}`;

      const popup = _api.openPopup(authUrl, 'authPopup', 600, 600);

      popup.addEventListener('close', reject, false);

      window.addEventListener(
        'message',
        (event) => {
          if (
            _get(event, 'data.name') === 'wix-get-token' &&
            _get(event, 'data.success')
          ) {
            popup.close();
            resolve();
          } else {
            reject();
          }
        },
        false,
      );
    });
  }

  return {
    getActions,
    updateStatus,
    _mainAction,
  };
}
